import { FC, useMemo } from 'react';
import { Box, Text, Flex } from 'src/components/ui';
import {
  ICreative,
  isCreatomateAttributes,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import { IChannelCreativeAttributes } from 'src/lib/schemas';

import FusionLoading from 'src/components/common/FusionLoading';
import TemplatePreview from 'src/components/templates/TemplatePreview';
import CreativeCard from '../CreativeCard';
import MagazinePageMockup from './MagazinePageMockup';

// Placement display names, aspect ratios, and target dimensions for Creatomate
const PLACEMENT_CONFIG: Record<string, {
  title: string;
  aspectRatio: number;
  width: number;
  height: number;
}> = {
  magazineFullPage: { title: 'Full Page', aspectRatio: 2588 / 3338, width: 2588, height: 3338 },
  magazineHalfHorizontal: { title: '1/2 Horizontal', aspectRatio: 2250 / 1388, width: 2250, height: 1388 },
  magazineHalfVertical: { title: '1/2 Vertical', aspectRatio: 1463 / 2138, width: 1463, height: 2138 },
  magazineThirdSquare: { title: '1/3 Square', aspectRatio: 1463 / 1388, width: 1463, height: 1388 },
};

interface MagazineAdCreativeProps {
  creative: ICreative;
  withActions?: boolean;
  size?: string;
  creativesConfig?: IChannelCreativeAttributes[];
}

const MagazineAdCreative: FC<MagazineAdCreativeProps> = ({
  creative,
  withActions = true,
  size = 'md',
  creativesConfig,
}) => {
  const isPending =
    creative.status === 'pending' || creative.status === 'generating';

  const isCreatomate =
    creative.adEngine === 'creatomate' &&
    isCreatomateAttributes(creative.attributes);

  // Get placement info
  const placement = creative.placements?.[0] || 'magazineHalfHorizontal';
  const placementConfig = PLACEMENT_CONFIG[placement] || PLACEMENT_CONFIG.magazineHalfHorizontal;

  // Calculate container height - use consistent height for all placements
  const getContainerHeight = () => {
    return { base: '380px', md: '480px' };
  };

  const normalizedTemplate = useMemo(() => {
    if (isCreatomate) {
      // For magazine ads, the actual data may be in attributes.output (from the magazine-ad API response)
      // If output exists and has elements, use that instead of the base attributes
      const attrs = creative.attributes as any;

      // Check if we have actual magazine ad output or are falling back to design direction
      const hasRealOutput = attrs.output && Array.isArray(attrs.output.elements) && attrs.output.elements.length > 0;

      console.log('[MagazineAd] Building template:', {
        hasOutput: hasRealOutput,
        outputElements: attrs.output?.elements?.length,
        baseElements: attrs.elements?.length,
        templateId: attrs.templateId,
        usingTemplateId: !hasRealOutput, // Will use templateId for Creatomate CDN
      });

      const sourceData = (attrs.output && Array.isArray(attrs.output.elements) && attrs.output.elements.length > 0)
        ? attrs.output
        : attrs;

      // Get source dimensions for scaling calculations
      const sourceWidth = sourceData.width || attrs.width || 2250;
      const sourceHeight = sourceData.height || attrs.height || 1388;

      // Target dimensions based on placement
      const targetWidth = placementConfig.width;
      const targetHeight = placementConfig.height;

      // Calculate scale factors
      const scaleX = targetWidth / sourceWidth;
      const scaleY = targetHeight / sourceHeight;

      const clone = JSON.parse(JSON.stringify({
        elements: sourceData.elements || [],
        // Use placement-specific dimensions so Creatomate renders at correct aspect ratio
        width: targetWidth,
        height: targetHeight,
        fonts: sourceData.fonts || attrs.fonts || [],
        // Include templateId when using design direction elements (no real magazine ad output)
        // This allows Creatomate to load images from its CDN instead of direct fetch (CORS issues)
        ...(hasRealOutput ? {} : { templateId: attrs.templateId }),
      }));

      // Scale all element positions and sizes to fit the new dimensions
      const scaleElement = (el: any): any => {
        const scaled = { ...el };

        // Scale position
        if (typeof scaled.x === 'number') scaled.x = Math.round(scaled.x * scaleX);
        if (typeof scaled.y === 'number') scaled.y = Math.round(scaled.y * scaleY);

        // Scale size
        if (typeof scaled.width === 'number') scaled.width = Math.round(scaled.width * scaleX);
        if (typeof scaled.height === 'number') scaled.height = Math.round(scaled.height * scaleY);

        // Scale font size
        if (typeof scaled.font_size === 'number') {
          scaled.font_size = Math.round(scaled.font_size * Math.min(scaleX, scaleY));
        }

        // Scale nested elements
        if (scaled.elements && Array.isArray(scaled.elements)) {
          scaled.elements = scaled.elements.map(scaleElement);
        }

        return scaled;
      };

      console.log('[MagazineAd] Scaling template:', {
        source: `${sourceWidth}x${sourceHeight}`,
        target: `${targetWidth}x${targetHeight}`,
        scaleX: scaleX.toFixed(2),
        scaleY: scaleY.toFixed(2),
        placement,
      });

      console.log('[MagazineAd] Clone before processing:', {
        elementsCount: clone.elements?.length,
        imageElements: clone.elements?.filter((e: any) => e.type === 'image').length,
      });

      if (Array.isArray(clone.elements)) {
        clone.elements = clone.elements.map((el: any) => {
          let processed = { ...el };

          // Trim string values
          if (typeof processed.x === 'string') processed.x = processed.x.trim();
          if (typeof processed.y === 'string') processed.y = processed.y.trim();
          if (typeof processed.width === 'string') processed.width = processed.width.trim();
          if (typeof processed.height === 'string') processed.height = processed.height.trim();

          // Process nested elements
          if (processed.elements && Array.isArray(processed.elements)) {
            processed.elements = processed.elements.map((subEl: any) => {
              const processedSub = { ...subEl };
              if (typeof processedSub.x === 'string') processedSub.x = processedSub.x.trim();
              if (typeof processedSub.y === 'string') processedSub.y = processedSub.y.trim();
              if (typeof processedSub.width === 'string')
                processedSub.width = processedSub.width.trim();
              if (typeof processedSub.height === 'string')
                processedSub.height = processedSub.height.trim();
              return processedSub;
            });
          }

          // Scale the element to fit the new dimensions
          processed = scaleElement(processed);

          return processed;
        });
      }

      console.log('[MagazineAd] Final template:', {
        elementsCount: clone.elements?.length,
        imageElements: clone.elements?.filter((e: any) => e.type === 'image').length,
        shapeElements: clone.elements?.filter((e: any) => e.type === 'shape').length,
        hasTemplateId: !!clone.templateId,
        templateId: clone.templateId,
      });

      return clone;
    }
    return null;
  }, [JSON.stringify(creative.attributes), placement, placementConfig.width, placementConfig.height]);

  // Extract text attributes for display below the preview
  // Use output data if available (from magazine-ad API), otherwise fall back to base attributes
  const attrs = (creative as any).attributes || {};
  const textSource = attrs.output || attrs;
  const { headline, subheadline, bodyText, callToAction, contactInfo } = textSource;

  return (
    <CreativeCard
      creative={creative}
      withActions={withActions}
      size="lg"
      title={placementConfig.title}
      creativesConfig={creativesConfig}
    >
      {isPending ? (
        <Flex width="100%" minH={{ base: '300px', md: '400px' }} justifyContent="center" alignItems="center">
          <FusionLoading isLoading />
        </Flex>
      ) : (
        <Box p={{ base: 3, md: 4 }} width="100%">
          {/* Magazine Ad Preview with Page Mockup */}
          <Box
            border="2px solid"
            borderColor="gray.300"
            borderRadius="md"
            overflow="hidden"
            bg="gray.50"
          >
            <MagazinePageMockup
              placement={placement}
              height={getContainerHeight()}
            >
              {isCreatomate && normalizedTemplate && normalizedTemplate.elements?.length > 0 ? (
                <TemplatePreview
                  key={JSON.stringify(normalizedTemplate.elements)}
                  template={normalizedTemplate}
                />
              ) : (
                <Flex
                  h="full"
                  align="center"
                  justify="center"
                  bg="white"
                  flexDirection="column"
                  gap={2}
                  p={3}
                >
                  {/* Fallback: Display text content when no template */}
                  {headline && (
                    <Text
                      fontSize={{ base: 'sm', md: 'md' }}
                      fontWeight="bold"
                      textAlign="center"
                      color="gray.800"
                      noOfLines={2}
                    >
                      {headline}
                    </Text>
                  )}
                  {subheadline && (
                    <Text
                      fontSize={{ base: 'xs', md: 'sm' }}
                      fontWeight="semibold"
                      textAlign="center"
                      color="gray.600"
                      noOfLines={2}
                    >
                      {subheadline}
                    </Text>
                  )}
                  {bodyText && (
                    <Text
                      fontSize="xs"
                      textAlign="center"
                      color="gray.600"
                      noOfLines={3}
                    >
                      {bodyText}
                    </Text>
                  )}
                  {callToAction && (
                    <Box
                      bg="red.500"
                      color="white"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontWeight="bold"
                      fontSize="xs"
                    >
                      {callToAction}
                    </Box>
                  )}
                </Flex>
              )}
            </MagazinePageMockup>
          </Box>

          {/* Print Info Badge */}
          <Flex
            mt={3}
            justify="space-between"
            align="center"
            px={2}
          >
            <Text fontSize="xs" color="gray.500">
              Print Size: {placementConfig.title}
            </Text>
            <Text fontSize="xs" color="gray.500">
              300 DPI
            </Text>
          </Flex>
        </Box>
      )}
    </CreativeCard>
  );
};

export default MagazineAdCreative;
